import React, { useState, useMemo, useEffect, useCallback } from "react";
import { SegmentedControl, Button } from "@deere/isg.component-library";
import "./HeartbeatTimeline.css";

const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

interface HeartbeatLogEntry {
  topic: string;
  message: string;
  receivedAt: string;
}

type LatencyLevel = "normal" | "warning" | "critical";

const LATENCY_COLORS: Record<LatencyLevel, string> = {
  normal: "#4caf50",
  warning: "#ff9800",
  critical: "#f44336",
};

const LATENCY_LABELS: Record<LatencyLevel, string> = {
  normal: "Normal (<1s)",
  warning: "Warning (1–2s)",
  critical: "Critical (≥2s)",
};

const TIME_RANGE_OPTIONS = [
  { label: "5m", ms: 5 * 60 * 1000 },
  { label: "15m", ms: 15 * 60 * 1000 },
  { label: "1h", ms: 60 * 60 * 1000 },
  { label: "4h", ms: 4 * 60 * 60 * 1000 },
];

// Latency thresholds (ms)
const WARN_MS = 1000;
const CRIT_MS = 2000;
const Y_AXIS_MAX_MS = 5000;
// Intervals longer than this are treated as intentional disconnects and
// excluded from the chart and the alert list.
const SESSION_GAP_MS = 60 * 1000;

function classifyDelta(deltaMs: number): LatencyLevel {
  if (deltaMs >= CRIT_MS) return "critical";
  if (deltaMs >= WARN_MS) return "warning";
  return "normal";
}

function formatTimeLabel(ms: number): string {
  const date = new Date(ms);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

interface LatencyPoint {
  t: number; // receivedAt timestamp (ms epoch) of the second heartbeat
  delta: number; // interval since previous heartbeat (ms)
  level: LatencyLevel;
}

// SVG plot geometry (viewBox units; CSS scales to container width)
const PLOT_W = 800;
const PLOT_H = 220;
const PLOT_PAD_L = 48;
const PLOT_PAD_R = 12;
const PLOT_PAD_T = 12;
const PLOT_PAD_B = 28;
const INNER_W = PLOT_W - PLOT_PAD_L - PLOT_PAD_R;
const INNER_H = PLOT_H - PLOT_PAD_T - PLOT_PAD_B;

function HeartbeatTimeline({ currentTopicString }: { currentTopicString: string }) {
  const [selectedRangeIndex, setSelectedRangeIndex] = useState(0);
  const [heartbeatLogs, setHeartbeatLogs] = useState<HeartbeatLogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!currentTopicString || !backendURI) return;

    const rangeDef = TIME_RANGE_OPTIONS[selectedRangeIndex];
    const since = new Date(Date.now() - rangeDef.ms).toISOString();

    try {
      setLoading(true);
      const res = await fetch(`https://${backendURI}/HeartbeatLogs/fetch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          topic: currentTopicString,
          since,
          limit: 2000,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setHeartbeatLogs(Array.isArray(data) ? data : data.logs ?? []);
      }
    } catch {
      // silently fail — will retry on next interval
    } finally {
      setLoading(false);
    }
  }, [currentTopicString, selectedRangeIndex]);

  // Fetch on mount and whenever the selected time range or topic changes.
  // No auto-refresh — the user clicks the Refresh button to pull fresh data.
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Parse, sort ascending by receivedAt, and dedupe by (topic, message).
  // The published heartbeat payload is a monotonic counter, so duplicate
  // (topic, counter) pairs represent the same publish received twice (e.g.,
  // via re-subscription on broker reconnect) and would otherwise halve the
  // computed average interval.
  const processedLogs = useMemo(() => {
    const sorted = heartbeatLogs
      .map((log) => ({
        receivedAt: new Date(log.receivedAt).getTime(),
        topic: log.topic,
        message: log.message,
      }))
      .sort((a, b) => a.receivedAt - b.receivedAt);

    const seen = new Set<string>();
    const deduped: typeof sorted = [];
    for (const log of sorted) {
      const key = `${log.topic}\u0000${log.message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(log);
    }
    return deduped;
  }, [heartbeatLogs]);

  // Compute the visible time window
  const { rangeStart, rangeEnd } = useMemo(() => {
    const now = Date.now();
    const rangeDef = TIME_RANGE_OPTIONS[selectedRangeIndex];
    return { rangeStart: now - rangeDef.ms, rangeEnd: now };
  }, [selectedRangeIndex]);

  // Build per-heartbeat latency points. Skip intervals that span a
  // session boundary (assumed intentional disconnects).
  const latencyPoints = useMemo<LatencyPoint[]>(() => {
    const points: LatencyPoint[] = [];
    for (let i = 1; i < processedLogs.length; i++) {
      const prev = processedLogs[i - 1].receivedAt;
      const curr = processedLogs[i].receivedAt;
      const delta = curr - prev;
      if (delta > SESSION_GAP_MS) continue;
      points.push({ t: curr, delta, level: classifyDelta(delta) });
    }
    return points;
  }, [processedLogs]);

  // Alert list: any heartbeat whose interval exceeded the warning threshold,
  // newest first. Already filtered against SESSION_GAP_MS by latencyPoints.
  const alertEntries = useMemo(() => {
    return latencyPoints
      .filter((p) => p.delta >= WARN_MS)
      .slice()
      .sort((a, b) => b.t - a.t);
  }, [latencyPoints]);

  const totalDuration = rangeEnd - rangeStart;

  // Map a timestamp to an x coordinate in the plot
  const xFor = (t: number) =>
    PLOT_PAD_L + ((t - rangeStart) / totalDuration) * INNER_W;
  // Map a latency in ms to a y coordinate in the plot (clamped to the axis cap)
  const yFor = (deltaMs: number) =>
    PLOT_PAD_T + (1 - Math.min(deltaMs, Y_AXIS_MAX_MS) / Y_AXIS_MAX_MS) * INNER_H;

  // Group consecutive points by classification level so we can render each
  // run as its own colored polyline. A run breaks when the level changes or
  // when there's a session gap (handled by the polyline ending).
  const polylineRuns = useMemo(() => {
    const runs: { level: LatencyLevel; points: LatencyPoint[] }[] = [];
    let current: { level: LatencyLevel; points: LatencyPoint[] } | null = null;
    for (const p of latencyPoints) {
      if (!current || current.level !== p.level) {
        // Start a new run; include the previous run's last point so segments
        // visually connect across color transitions.
        const seed: LatencyPoint[] = current ? [current.points[current.points.length - 1]] : [];
        current = { level: p.level, points: [...seed, p] };
        runs.push(current);
      } else {
        current.points.push(p);
      }
    }
    return runs;
  }, [latencyPoints]);

  // Axis tick labels (5 evenly spaced)
  const xTicks = useMemo(() => {
    const tickCount = 4;
    const ticks: { x: number; label: string }[] = [];
    for (let i = 0; i <= tickCount; i++) {
      const t = rangeStart + (totalDuration * i) / tickCount;
      ticks.push({ x: xFor(t), label: formatTimeLabel(t) });
    }
    return ticks;
  }, [rangeStart, rangeEnd]);

  const yTicks = [0, 1000, 2000, 3000, 4000, 5000];

  // Summary stats (avg/max from non-session-gap intervals)
  const summary = useMemo(() => {
    if (latencyPoints.length === 0) {
      return { total: processedLogs.length, avgInterval: 0, maxInterval: 0 };
    }
    let sum = 0;
    let max = 0;
    for (const p of latencyPoints) {
      sum += p.delta;
      if (p.delta > max) max = p.delta;
    }
    return {
      total: processedLogs.length,
      avgInterval: Math.round(sum / latencyPoints.length),
      maxInterval: max,
    };
  }, [latencyPoints, processedLogs]);

  const segmentedControlButtons = TIME_RANGE_OPTIONS.map((opt, idx) => ({
    name: opt.label,
    onClickHandler: () => setSelectedRangeIndex(idx),
  }));

  const thresholdY = yFor(CRIT_MS);

  return (
    <div className="heartbeat-timeline">
      <h3>Heartbeat Timeline {loading && <span className="heartbeat-loading">⟳</span>}</h3>

      {/* Time range selector */}
      <div className="heartbeat-range-selector">
        <SegmentedControl
          buttons={segmentedControlButtons}
          selectedIndex={selectedRangeIndex}
        />
      </div>

      {/* Summary stats */}
      <div className="heartbeat-summary">
        <div className="heartbeat-summary-item">
          <span className="heartbeat-summary-label">Heartbeats</span>
          <span className="heartbeat-summary-value">{summary.total}</span>
        </div>
        <div className="heartbeat-summary-item">
          <span className="heartbeat-summary-label">Avg Interval</span>
          <span className="heartbeat-summary-value">
            {summary.avgInterval > 0 ? `${summary.avgInterval}ms` : "—"}
          </span>
        </div>
        <div className="heartbeat-summary-item">
          <span className="heartbeat-summary-label">Max Interval</span>
          <span className="heartbeat-summary-value">
            {summary.maxInterval > 0 ? `${summary.maxInterval}ms` : "—"}
          </span>
        </div>
      </div>

      {/* Refresh + Export buttons */}
      <div className="heartbeat-actions">
        <Button
          variant="secondary"
          size="small"
          disabled={loading}
          loading={loading}
          onClick={fetchLogs}
        >
          Refresh
        </Button>
        <Button
          variant="secondary"
          size="small"
          disabled={heartbeatLogs.length === 0}
          onClick={() => {
            const escapeCsv = (val: string) => {
              if (/[",\n\r]/.test(val)) {
                return `"${val.replace(/"/g, '""')}"`;
              }
              return val;
            };
            const csv = [
              ["receivedAt", "topic", "message"].join(","),
              ...heartbeatLogs.map((log) =>
                [
                  escapeCsv(log.receivedAt),
                  escapeCsv(log.topic),
                  escapeCsv(log.message),
                ].join(","),
              ),
            ].join("\n");
            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `heartbeat-logs-${currentTopicString.replace(/\//g, "_")}-${new Date()
              .toISOString()
              .slice(0, 19)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          Export Logs ({heartbeatLogs.length})
        </Button>
      </div>

      {/* Latency chart */}
      <svg
        className="heartbeat-chart"
        viewBox={`0 0 ${PLOT_W} ${PLOT_H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Heartbeat latency over time"
      >
        {/* Plot background */}
        <rect
          x={PLOT_PAD_L}
          y={PLOT_PAD_T}
          width={INNER_W}
          height={INNER_H}
          className="heartbeat-chart-bg"
        />

        {/* Y-axis grid + labels */}
        {yTicks.map((ms) => {
          const y = yFor(ms);
          return (
            <g key={`y-${ms}`}>
              <line
                x1={PLOT_PAD_L}
                x2={PLOT_PAD_L + INNER_W}
                y1={y}
                y2={y}
                className="heartbeat-chart-grid"
              />
              <text
                x={PLOT_PAD_L - 6}
                y={y}
                className="heartbeat-chart-axis-label"
                textAnchor="end"
                dominantBaseline="middle"
              >
                {ms === 0 ? "0" : `${ms / 1000}s`}
              </text>
            </g>
          );
        })}

        {/* X-axis labels */}
        {xTicks.map((tick, i) => (
          <text
            key={`x-${i}`}
            x={tick.x}
            y={PLOT_PAD_T + INNER_H + 16}
            className="heartbeat-chart-axis-label"
            textAnchor={i === 0 ? "start" : i === xTicks.length - 1 ? "end" : "middle"}
          >
            {tick.label}
          </text>
        ))}

        {/* Critical threshold line at 2000ms */}
        <line
          x1={PLOT_PAD_L}
          x2={PLOT_PAD_L + INNER_W}
          y1={thresholdY}
          y2={thresholdY}
          className="heartbeat-chart-threshold"
        />
        <text
          x={PLOT_PAD_L + INNER_W - 4}
          y={thresholdY - 4}
          className="heartbeat-chart-threshold-label"
          textAnchor="end"
        >
          2s failure threshold
        </text>

        {/* Latency polylines, colored per run */}
        {polylineRuns.map((run, i) => {
          if (run.points.length < 2) return null;
          const d = run.points
            .map((p) => `${xFor(p.t).toFixed(2)},${yFor(p.delta).toFixed(2)}`)
            .join(" ");
          return (
            <polyline
              key={`run-${i}`}
              points={d}
              fill="none"
              stroke={LATENCY_COLORS[run.level]}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })}

        {/* Data points */}
        {latencyPoints.map((p, i) => (
          <circle
            key={`pt-${i}`}
            cx={xFor(p.t)}
            cy={yFor(p.delta)}
            r={2}
            fill={LATENCY_COLORS[p.level]}
          >
            <title>{`${formatTimeLabel(p.t)} — ${p.delta}ms`}</title>
          </circle>
        ))}
      </svg>

      {/* Legend */}
      <div className="heartbeat-legend">
        {(["normal", "warning", "critical"] as const).map((level) => (
          <div key={level} className="heartbeat-legend-item">
            <span
              className="heartbeat-legend-color"
              style={{ backgroundColor: LATENCY_COLORS[level] }}
            />
            <span className="heartbeat-legend-label">{LATENCY_LABELS[level]}</span>
          </div>
        ))}
      </div>

      {/* Alert list: warning + critical heartbeats */}
      <div className="heartbeat-alerts">
        <div className="heartbeat-alerts-header">
          Latency alerts ({alertEntries.length})
        </div>
        {alertEntries.length === 0 ? (
          <div className="heartbeat-alerts-empty">No heartbeats above 1s in selected range.</div>
        ) : (
          <ul className="heartbeat-alerts-list">
            {alertEntries.map((entry, i) => (
              <li
                key={`alert-${i}`}
                className={`heartbeat-alert-item heartbeat-alert-item--${entry.level}`}
              >
                <span className="heartbeat-alert-time">{formatTimeLabel(entry.t)}</span>
                <span className="heartbeat-alert-delta">{entry.delta}ms</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default HeartbeatTimeline;
